import { useEffect, useRef } from 'react';
import { useMessagingStore } from '@/store/messaging.store';
import { hasOpenDialog } from '@/utils/open-dialogs';
import { useConversations, useUnreadSummary } from '@/hooks/useMessages';
import { useMessagingSocket } from '@/hooks/useMessagingSocket';
import { ConversationList } from './ConversationList';
import { ConversationView } from './ConversationView';
import { NewConversationModal } from './NewConversationModal';

import { IconButton } from '../common/Button';
import FaIcon from '../common/FaIcon';

export function MessagingPanel() {
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    isPanelOpen,
    setPanelOpen,
    activeConversationId,
    setActiveConversation,
    setConversations,
    isNewConversationModalOpen,
    setNewConversationModalOpen,
    isConnected,
  } = useMessagingStore();

  const { data: conversationsData } = useConversations();
  const { data: unreadData } = useUnreadSummary();

  // Initialize socket connection
  useMessagingSocket();

  // Update store when conversations load
  useEffect(() => {
    if (conversationsData?.data) {
      setConversations(conversationsData.data);
    }
  }, [conversationsData, setConversations]);

  // The panel opens over a scrim and traps the page behind it, so focus has to
  // follow it in — otherwise the next Tab continues in the dashboard underneath.
  useEffect(() => {
    if (isPanelOpen) panelRef.current?.focus();
  }, [isPanelOpen]);

  // Close on escape.
  //
  // This listener sits on `document`, so it sees Escape even when focus is
  // nowhere in particular — necessary, but it means the panel would otherwise
  // answer for keypresses aimed at whatever is stacked on top of it. One press
  // tore down a modal *and* the whole panel, taking any half-composed
  // conversation with it.
  //
  // The rule is positional rather than a list of flags: if any dialog is open
  // above the panel, that dialog owns Escape. Enumerating open-state (the New
  // Conversation flag lives in the store; Forward, Settings and Search are
  // local to ConversationView) would need extending by every future modal, and
  // would be silently wrong until someone noticed.
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || !isPanelOpen) return;
      if (hasOpenDialog(panelRef.current)) return;

      if (activeConversationId) {
        setActiveConversation(null);
      } else {
        setPanelOpen(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isPanelOpen, activeConversationId, setPanelOpen, setActiveConversation]);

  if (!isPanelOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-25 z-40"
        aria-hidden="true"
        onClick={() => setPanelOpen(false)}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="messaging-panel-title"
        tabIndex={-1}
        className="fixed right-0 top-0 h-full w-full sm:w-96 md:w-[420px] bg-surface-elevated shadow-xl z-50 flex flex-col outline-none"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-theme-primary bg-surface-secondary">
          <div className="flex items-center gap-3">
            {activeConversationId && (
              <IconButton
                icon={<FaIcon icon="chevron-left" className="w-5 h-5" />}
                label="Back to conversations"
                variant="ghost"
                size="sm"
                onClick={() => setActiveConversation(null)}
              />
            )}
            <h2 id="messaging-panel-title" className="heading-3">
              {activeConversationId ? 'Conversation' : 'Messages'}
            </h2>
            {!activeConversationId && unreadData && unreadData.total_unread > 0 && (
              <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {unreadData.total_unread}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Connection indicator */}
            {/* Red vs green was the only channel here, and `title` on a bare
                div is not reliably announced. Keep the dot, add the words. */}
            <div className="flex items-center gap-1.5" role="status">
              <span
                aria-hidden="true"
                className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}
              />
              <span className="sr-only">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
            {!activeConversationId && (
              <IconButton
                icon={<FaIcon icon="plus" className="w-5 h-5" />}
                label="New conversation"
                variant="ghost"
                size="sm"
                onClick={() => setNewConversationModalOpen(true)}
              />
            )}
            <IconButton
              icon={<FaIcon icon="xmark" className="w-5 h-5" />}
              label="Close messages"
              variant="ghost"
              size="sm"
              onClick={() => setPanelOpen(false)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeConversationId ? (
            <ConversationView conversationId={activeConversationId} />
          ) : (
            <ConversationList />
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      {isNewConversationModalOpen && (
        <NewConversationModal
          onClose={() => setNewConversationModalOpen(false)}
          onCreated={(conversationId) => {
            setNewConversationModalOpen(false);
            setActiveConversation(conversationId);
          }}
        />
      )}
    </>
  );
}
